import React, { useState } from "react";
import { v4 as uuidv4 } from "uuid";
import dayjs from "dayjs";

import VectorSource from "ol/source/Vector";
import VectorLayer from "ol/layer/Vector";
import Polygon from "ol/geom/Polygon";
import Feature from "ol/Feature";
import Draw, { createBox } from "ol/interaction/Draw";

import LocationSearchingIcon from "@mui/icons-material/LocationSearching";
import HomeIcon from "@mui/icons-material/Home";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";

import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import InputAdornment from "@mui/material/InputAdornment";
import Autocomplete from "@mui/material/Autocomplete";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import Fab from "@mui/material/Fab";
import useMediaQuery from "@mui/material/useMediaQuery";
import Collapse from "@mui/material/Collapse";

import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import { getSourceIdsFromUserInput, GetTimezone } from "../../util";

import MapFullScreen from "../../map/mapFullScreen";

import useOpenLayersMap from "../../hooks/useOpenLayersMap";

import { useUserAuth } from "../../contexts/authContext";

const AirspaceCharacterization = ({ setCreatingReportType, getReports }) => {
    const [timeStartDayjs, setTimeStartDayjs] = useState(dayjs().subtract(30, "days"));
    const [timeStartErrorText, setTimeStartErrorText] = useState("");

    const [timeEndDayjs, setTimeEndDayjs] = useState(dayjs());
    const [timeEndErrorText, setTimeEndErrorText] = useState("");

    const [altitudeMinMslFt, setAltitudeMinMslFt] = useState("0");
    const [altitudeMinErrorText, setAltitudeMinErrorText] = useState("");

    const [altitudeMaxMslFt, setAltitudeMaxMslFt] = useState("400");
    const [altitudeMaxErrorText, setAltitudeMaxErrorText] = useState("");

    const [sourceType, setSourceType] = useState("GROUND_BASED_RADAR");
    const [sourceIds, setSourceIds] = useState([]);
    const [sourceId, setSourceId] = useState("");

    const [airspaceBounds, setAirspaceBounds] = useState({ min_lat_deg: "", max_lat_deg: "", min_lon_deg: "", max_lon_deg: "" });
    const [airspaceBoundsErrorText, setAirspaceBoundsErrorText] = useState({ min_lat_deg: "", max_lat_deg: "", min_lon_deg: "", max_lon_deg: "" });
    const [airspaceFeature, setAirspaceFeature] = useState(null);

    const [drawing, setDrawing] = useState(false);
    const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

    const { user, handleFailedFetch, userMapSettings } = useUserAuth();

    const MAP_ID = "airspace-analysis-map";
    const map = useOpenLayersMap(MAP_ID, false);
    const isDesktop = useMediaQuery("(min-width:900px)");

    const handleSourceIdKeyDown = (e) => {
        const value = getSourceIdsFromUserInput(e);
        if (value.length < 1) {
            return;
        }
        if (e.key === " " || e.key === "," || e.key === "Enter") {
            setSourceIds((prev) => [...prev, value]);
            setSourceId("");
        } else {
            setSourceId(value);
        }
    };
    const handleSourceIdBlur = (e) => {
        const value = getSourceIdsFromUserInput(e);
        if (value.length < 1) {
            return;
        }
        setSourceIds((prev) => [...prev, value]);
        setSourceId("");
    };
    const handleSourceIdChange = (e) => {
        const value = getSourceIdsFromUserInput(e);
        setSourceId(value);
    };
    const handleSourceIdsChange = (e, value) => {
        if (typeof sourceId === "string" && sourceId.length > 0) {
            setSourceId("");
        }
        setSourceIds(value);
    };
    const handleClickToDrawButtonClick = () => {
        if (!map) return;
        if (isDesktop === false) setMobileDrawerOpen(false);

        const source = new VectorSource({ wrapX: false });
        const layer = new VectorLayer({ source: source });
        map.addLayer(layer);

        const draw = new Draw({
            source: source,
            type: "Circle",
            geometryFunction: createBox()
        });
        map.addInteraction(draw);
        setDrawing(true);

        draw.on("drawend", ({ feature }) => {
            map.removeInteraction(draw);
            setDrawing(false);

            const geometry = feature.getGeometry();
            if (geometry) {
                const coordinates = geometry.getCoordinates();
                if (Array.isArray(coordinates) && coordinates.length > 0 && Array.isArray(coordinates[0])) {
                    let minLat = Number.MAX_SAFE_INTEGER;
                    let maxLat = Number.MIN_SAFE_INTEGER;
                    let minLon = Number.MAX_SAFE_INTEGER;
                    let maxLon = Number.MIN_SAFE_INTEGER;

                    coordinates[0].forEach(([longitude, latitude]) => {
                        const lng = parseFloat(longitude.toFixed(4));
                        const lat = parseFloat(latitude.toFixed(4));

                        if (lat < minLat) {
                            minLat = lat.toString();
                        }
                        if (lat > maxLat) {
                            maxLat = lat.toString();
                        }
                        if (lng < minLon) {
                            minLon = lng.toString();
                        }
                        if (lng > maxLon) {
                            maxLon = lng.toString();
                        }
                    });
                    if (maxLat - minLat < 1 && maxLon - minLon < 1) {
                        map.getView().fit(geometry, {
                            padding: [100, 100, 100, 100]
                        });
                        setAirspaceBounds({
                            min_lat_deg: minLat,
                            max_lat_deg: maxLat,
                            min_lon_deg: minLon,
                            max_lon_deg: maxLon
                        });
                        setAirspaceFeature(feature);
                    } else {
                        alert("The difference between the minimum and maximum latitude or longitude can not exceed 1 degree. Please try again.");
                        handleRemoveAirspaceFeature();
                        handleClickToDrawButtonClick();
                    }
                }
            }
        });
    };
    const handleDegreeChange = (e) => {
        const { name, value } = e.target;
        if (/^-?\d*\.?\d*$/.test(value) === false) {
            return;
        }

        let minLat = airspaceBounds.min_lat_deg;
        let maxLat = airspaceBounds.max_lat_deg;
        let minLon = airspaceBounds.min_lon_deg;
        let maxLon = airspaceBounds.max_lon_deg;

        setAirspaceBounds((prev) => {
            minLat = name === "min_lat_deg" ? value : prev.min_lat_deg;
            maxLat = name === "max_lat_deg" ? value : prev.max_lat_deg;
            minLon = name === "min_lon_deg" ? value : prev.min_lon_deg;
            maxLon = name === "max_lon_deg" ? value : prev.max_lon_deg;

            const minLatNum = parseFloat(minLat);
            const maxLatNum = parseFloat(maxLat);
            const minLonNum = parseFloat(minLon);
            const maxLonNum = parseFloat(maxLon);
            const coordinates = [
                [
                    [minLonNum, minLatNum],
                    [maxLonNum, minLatNum],
                    [maxLonNum, maxLatNum],
                    [minLonNum, maxLatNum],
                    [minLonNum, minLatNum]
                ]
            ];

            const validCoordinates = coordinates[0].every(([lng, lat]) => isNaN(lng) === false && isNaN(lat) === false);
            if (map && validCoordinates) {
                if (airspaceFeature) {
                    const geometry = airspaceFeature.getGeometry();
                    if (geometry) {
                        geometry.setCoordinates(coordinates);
                        map.getView().fit(geometry, { padding: [100, 100, 100, 100] });
                    }
                } else {
                    const feature = new Feature({ geometry: new Polygon(coordinates) });
                    setAirspaceFeature(feature);

                    const source = new VectorSource({ wrapX: false, features: [feature] });
                    const layer = new VectorLayer({ source: source });
                    map.addLayer(layer);

                    const geometry = feature.getGeometry();
                    if (geometry) {
                        map.getView().fit(geometry, { padding: [100, 100, 100, 100] });
                    }
                }
            } else if (airspaceFeature) {
                handleRemoveAirspaceFeature();
            }
            return {
                min_lat_deg: minLat,
                max_lat_deg: maxLat,
                min_lon_deg: minLon,
                max_lon_deg: maxLon
            };
        });
    };
    const handleRemoveAirspaceFeature = () => {
        if (!map) return;

        const layers = map.getLayers().getArray();
        layers.forEach((layer) => {
            if (layer.get("name") === "base") {
                return;
            }
            const source = layer.getSource();
            if (source) {
                source.clear();
            }
            map.removeLayer(layer);
        });
        if (airspaceFeature) {
            setAirspaceFeature(null);
        }
    };
    const handleClearBoundsButtonClick = () => {
        const defaultBounds = {
            min_lat_deg: "",
            max_lat_deg: "",
            min_lon_deg: "",
            max_lon_deg: ""
        };
        setAirspaceBounds(defaultBounds);
        handleRemoveAirspaceFeature();
    };
    const handleSubmitButtonClick = () => {
        let validSubmission = true;

        let minLat = parseFloat(airspaceBounds.min_lat_deg);
        if (isNaN(minLat) || minLat < -90 || minLat > 90) {
            setAirspaceBoundsErrorText((prev) => ({ ...prev, min_lat_deg: "Please enter a valid minimum latitude." }));
            validSubmission = false;
        } else if (airspaceBoundsErrorText) {
            setAirspaceBoundsErrorText((prev) => ({ ...prev, min_lat_deg: "" }));
        }

        let maxLat = parseFloat(airspaceBounds.max_lat_deg);
        let latDifference = maxLat - minLat;
        if (isNaN(maxLat) || maxLat < -90 || maxLat > 90 || maxLat < minLat || latDifference > 1) {
            setAirspaceBoundsErrorText((prev) => {
                return { ...prev, max_lat_deg: "Please enter a valid latitude no more than 1 degree greater than the minimum latitude." };
            });
            validSubmission = false;
        } else if (airspaceBoundsErrorText) {
            setAirspaceBoundsErrorText((prev) => ({ ...prev, max_lat_deg: "" }));
        }

        let minLon = parseFloat(airspaceBounds.min_lon_deg);
        if (isNaN(minLon) || minLon < -180 || minLon > 180) {
            setAirspaceBoundsErrorText((prev) => ({ ...prev, min_lon_deg: "Please enter a valid minimum longitude." }));
            validSubmission = false;
        } else if (airspaceBoundsErrorText) {
            setAirspaceBoundsErrorText((prev) => ({ ...prev, min_lon_deg: "" }));
        }

        let maxLon = parseFloat(airspaceBounds.max_lon_deg);
        let lonDifference = maxLon - minLon;
        if (isNaN(maxLon) || maxLon < -180 || maxLon > 180 || maxLon < minLon || lonDifference > 1) {
            setAirspaceBoundsErrorText((prev) => {
                return { ...prev, max_lon_deg: "Please enter a valid longitude no more than 1 degree greater than the minimum longitude." };
            });
            validSubmission = false;
        } else if (airspaceBoundsErrorText) {
            setAirspaceBoundsErrorText((prev) => ({ ...prev, max_lon_deg: "" }));
        }

        let minAltitude = parseFloat(altitudeMinMslFt);
        if (isNaN(minAltitude) || minAltitude < 0) {
            setAltitudeMinErrorText("Please enter a valid altitude.");
            validSubmission = false;
        } else if (altitudeMinErrorText) {
            setAltitudeMinErrorText("");
        }

        let maxAltitude = parseFloat(altitudeMaxMslFt);
        if (isNaN(maxAltitude) || maxAltitude < 1 || maxAltitude < minAltitude) {
            setAltitudeMaxErrorText("Please enter a number greater than the minimum altitude.");
            validSubmission = false;
        } else if (altitudeMaxErrorText) {
            setAltitudeMaxErrorText("");
        }

        if (!timeStartDayjs || !timeStartDayjs.isValid()) {
            setTimeStartErrorText("Please enter a valid date.");
            validSubmission = false;
        } else if (timeStartErrorText) {
            setTimeStartErrorText("");
        }

        if (!timeEndDayjs || !timeEndDayjs.isValid() || timeEndDayjs.isBefore(timeStartDayjs) || timeEndDayjs.diff(timeStartDayjs, "days") > 30) {
            setTimeEndErrorText("Please enter a date that is after and within 30 days of the start date.");
            validSubmission = false;
        } else if (timeEndErrorText) {
            setTimeEndErrorText("");
        }

        const currTime = dayjs().add(1, "minute");
        if (timeEndDayjs && timeEndDayjs.isAfter(currTime)) {
            setTimeEndErrorText("Date cannot be in the future.");
            validSubmission = false;
        } else if (timeEndErrorText) {
            setTimeEndErrorText("");
        }

        if (!validSubmission) {
            return;
        }

        const body = {
            uuid: uuidv4(),
            report_type: "AIRSPACE_CHARACTERIZATION",
            user_id: user.id,
            source_type: sourceType,
            time_start: timeStartDayjs.toISOString(),
            time_end: timeEndDayjs.toISOString(),
            lat_min: minLat,
            lat_max: maxLat,
            lon_min: minLon,
            lon_max: maxLon,
            alt_msl_ft_min: parseFloat(altitudeMinMslFt),
            alt_msl_ft_max: parseFloat(altitudeMaxMslFt)
        };
        if (Array.isArray(sourceIds) && sourceIds.length > 0) {
            body.source_ids = sourceIds;
        }
        const requestOptions = {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body)
        };
        fetch("/api/analysis/airspace/submit", requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then(() => {
                setCreatingReportType(null);
                getReports();
            })
            .catch((err) => handleFailedFetch(err));
    };
    const handleMinAltitudeChange = (e) => {
        const newMinAlt = e.target.value;
        if (/^-?\d*\.?\d*$/.test(newMinAlt)) {
            setAltitudeMinMslFt(newMinAlt);
        }
    };
    const handleMaxAltitudeChange = (e) => {
        const newMaxAlt = e.target.value;
        if (/^-?\d*\.?\d*$/.test(newMaxAlt)) {
            setAltitudeMaxMslFt(newMaxAlt);
        }
    };
    const handleHomeButtonClick = () => {
        if (map) {
            const view = map.getView();
            if (view) {
                const homeCoordinate = [userMapSettings.longitude, userMapSettings.latitude];
                view.setCenter(homeCoordinate);
                view.setZoom(12);
            }
        }
    };
    const handleJumpToFeatureButtonClick = () => {
        if (map && airspaceFeature) {
            const geometry = airspaceFeature.getGeometry();
            if (geometry) {
                map.getView().fit(geometry, { padding: [100, 100, 100, 100] });
            }
        }
    };
    return (
        <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "row" }}>
            <Collapse in={isDesktop || mobileDrawerOpen} orientation="horizontal">
                <Paper sx={{ display: "flex", flexDirection: "column", gap: 1, p: 2, width: { xs: "100%", md: "375px" }, height: "100%", overflowY: "auto" }}>
                    {isDesktop === false ? (
                        <Button endIcon={<KeyboardArrowRightIcon />} onClick={() => setMobileDrawerOpen(false)} sx={{ marginLeft: "auto" }}>
                            Back to map
                        </Button>
                    ) : (
                        <></>
                    )}
                    <Typography variant="body1">Sources</Typography>

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        <FormControl size="small" margin="dense" sx={{ width: "100%" }} disabled={drawing}>
                            <InputLabel id="select-source-type-label">Source Type</InputLabel>
                            <Select value={sourceType} label="Source Type" labelId="select-source-type-label" onChange={(e) => setSourceType(e.target.value)}>
                                {[
                                    "UNKNOWN",
                                    "TELEMETRY",
                                    "ADS_B",
                                    "GROUND_BASED_RADAR",
                                    "AIRBORNE_RADAR",
                                    "ACOUSTIC_SENSOR",
                                    "EO_IR",
                                    "MULTISENSOR_TRACKER",
                                    "REMOTE_ID"
                                ].map((value, i) => (
                                    <MenuItem key={i} value={value}>
                                        {value}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Autocomplete
                            multiple
                            options={[]}
                            value={sourceIds}
                            inputValue={sourceId}
                            onInputChange={handleSourceIdChange}
                            onChange={handleSourceIdsChange}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Source Ids (optional)"
                                    size="small"
                                    margin="dense"
                                    sx={{ width: "100%" }}
                                    value={sourceId}
                                    onKeyDown={handleSourceIdKeyDown}
                                    onBlur={handleSourceIdBlur}
                                />
                            )}
                            freeSolo
                            disabled={drawing}
                        />
                    </Box>

                    <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 1 }}>
                        <Typography variant="body1">{"Geographical Bounds"}</Typography>

                        {drawing === false && airspaceFeature === null ? (
                            <>
                                <Typography variant="body1">{" - "}</Typography>
                                <Link component="button" variant="body1" onClick={handleClickToDrawButtonClick}>
                                    click to draw
                                </Link>
                            </>
                        ) : drawing === false && airspaceFeature ? (
                            <>
                                <Typography variant="body1">{" - "}</Typography>
                                <Link component="button" variant="body1" onClick={handleClearBoundsButtonClick}>
                                    clear
                                </Link>
                            </>
                        ) : (
                            <></>
                        )}
                    </Box>
                    {[
                        {
                            name: "min_lat_deg",
                            label: "Latitude (min)",
                            value: airspaceBounds.min_lat_deg,
                            onChange: handleDegreeChange,
                            helperText: airspaceBoundsErrorText.min_lat_deg
                        },
                        {
                            name: "max_lat_deg",
                            label: "Latitude (max)",
                            value: airspaceBounds.max_lat_deg,
                            onChange: handleDegreeChange,
                            helperText: airspaceBoundsErrorText.max_lat_deg
                        },
                        {
                            name: "min_lon_deg",
                            label: "Longitude (min)",
                            value: airspaceBounds.min_lon_deg,
                            onChange: handleDegreeChange,
                            helperText: airspaceBoundsErrorText.min_lon_deg
                        },
                        {
                            name: "max_lon_deg",
                            label: "Longitude (max)",
                            value: airspaceBounds.max_lon_deg,
                            onChange: handleDegreeChange,
                            helperText: airspaceBoundsErrorText.max_lon_deg
                        }
                    ].map(({ name, label, value, onChange, helperText }, i) => (
                        <TextField
                            key={i}
                            label={label}
                            size="small"
                            margin="dense"
                            value={value}
                            onChange={onChange}
                            fullWidth
                            InputProps={{ endAdornment: <InputAdornment position="end">deg</InputAdornment> }}
                            disabled={drawing}
                            name={name}
                            error={helperText.length > 0}
                            helperText={helperText}
                        />
                    ))}

                    <Typography variant="body1">Altitude Range</Typography>

                    <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
                        <TextField
                            label="Altitude Min (MSL)"
                            sx={{ width: "100%" }}
                            onChange={handleMinAltitudeChange}
                            value={altitudeMinMslFt}
                            size="small"
                            margin="dense"
                            InputProps={{
                                endAdornment: <InputAdornment position="end">ft</InputAdornment>
                            }}
                            disabled={drawing}
                            error={altitudeMinErrorText.length > 0}
                            helperText={altitudeMinErrorText}
                        />

                        <TextField
                            label="Altitude Max (MSL)"
                            sx={{ width: "100%" }}
                            onChange={handleMaxAltitudeChange}
                            value={altitudeMaxMslFt}
                            size="small"
                            margin="dense"
                            InputProps={{
                                endAdornment: <InputAdornment position="end">ft</InputAdornment>
                            }}
                            disabled={drawing}
                            error={altitudeMaxErrorText.length > 0}
                            helperText={altitudeMaxErrorText}
                        />
                    </Box>

                    <Typography variant="body1">Date & Time</Typography>

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DateTimePicker
                                label={`Start Time (${GetTimezone()}) *`}
                                value={timeStartDayjs}
                                onChange={(value) => setTimeStartDayjs(value)}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        size: "small",
                                        margin: "dense",
                                        error: timeStartErrorText.length > 0,
                                        helperText: timeStartErrorText,
                                        inputProps: { "data-testid": "start_time" }
                                    }
                                }}
                                disabled={drawing}
                            />

                            <DateTimePicker
                                label={`End Time (${GetTimezone()}) *`}
                                value={timeEndDayjs}
                                onChange={(value) => setTimeEndDayjs(value)}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        size: "small",
                                        margin: "dense",
                                        error: timeEndErrorText.length > 0,
                                        helperText: timeEndErrorText,
                                        inputProps: { "data-testid": "end_time" }
                                    }
                                }}
                                disabled={drawing}
                            />
                        </LocalizationProvider>
                    </Box>

                    <Button onClick={handleSubmitButtonClick} variant="contained" sx={{ mt: 1 }} disabled={drawing} size="small">
                        Submit for analysis
                    </Button>
                    <Button onClick={() => setCreatingReportType(null)} sx={{ mt: 1 }} disabled={drawing} size="small">
                        Close
                    </Button>
                </Paper>
            </Collapse>

            <Box sx={{ height: "100%", flex: 1 }}>
                <MapFullScreen
                    map_id={MAP_ID}
                    map={map}
                    buttons={[
                        {
                            id: "center",
                            name: "Center",
                            visible: airspaceFeature ? true : false,
                            icon: <LocationSearchingIcon />,
                            onClick: handleJumpToFeatureButtonClick
                        },
                        {
                            id: "home",
                            name: "Home",
                            visible: true,
                            icon: <HomeIcon />,
                            onClick: handleHomeButtonClick
                        }
                    ]}
                />
                {!isDesktop && !mobileDrawerOpen ? (
                    <Fab size="medium" color="primary" onClick={() => setMobileDrawerOpen(true)} sx={{ position: "absolute", bottom: 8, left: 8 }}>
                        <KeyboardArrowLeftIcon />
                    </Fab>
                ) : (
                    <></>
                )}
            </Box>
        </Box>
    );
};

export default AirspaceCharacterization;
